import { useEffect, PropsWithChildren } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useSessionKf } from "components/auth/lib/useSessionKf";
import { DatadogLoggerProvider } from "./DatadogLoggerContext";

export function DatadogRumProvider({ children }: PropsWithChildren) {
  useDatadogRum();
  return <DatadogLoggerProvider>{children}</DatadogLoggerProvider>;
}

/**
 * Load datadog RUM client, including details about the user's authn session.
 */
function useDatadogRum() {
  const { session } = useSessionKf();
  const { configDatadogRum: isDatadogRumEnabled } = useFlags();

  useEffect(() => {
    if (
      !isDatadogRumEnabled ||
      globalThis.__ENV.RUNTIME_PUBLIC_DATADOG_ENV === "disabled"
    )
      return;
    const isRumInitialized = datadogRum.getInitConfiguration() !== undefined;
    if (!isRumInitialized) initDatadogRum();
    const user = datadogRum.getUser();
    if (!!session?.user) {
      if (user === undefined || user.id !== session.user.sub)
        datadogRum.setUser({
          id: session.user.sub,
        });
    } else {
      if (user !== undefined) datadogRum.clearUser();
    }
  }, [session?.user]);
}

function initDatadogRum() {
  datadogRum.init({
    site: "datadoghq.com",
    applicationId: process.env.NEXT_PUBLIC_DATADOG_RUM_APP_ID,
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
    env: globalThis.__ENV.RUNTIME_PUBLIC_DATADOG_ENV,
    version: globalThis.__ENV.RUNTIME_PUBLIC_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    useSecureSessionCookie: true,
  });
}
